import { Card, Flex, SimpleTable, Typography, Accordion } from "djuno-design";
import ExpandableCard from "../components/ExpandableCard";
import { GithubTypesLink } from "./../urls";

const { Title, Text } = Typography;

const AccordionPage = () => {
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>Accordion</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The Accordion component in Djuno Design allows for customizable
            collapsible panels. It supports various configurations such as
            custom panel classes, item loading states, and more. It is useful
            for presenting information in a compact and organized manner, where
            only one section is expanded at a time.
          </Text>

          <Title level={5}>Variants and Customization</Title>
          <Text size="sm">
            The Accordion component offers the following customization options:
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Items
                </Text>
                : An array of objects where each object represents an accordion
                item with a label and optional panel content.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  panelClassNames
                </Text>
                : Additional CSS classes to apply to the panel.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Loading
                </Text>
                : A boolean flag to indicate if the accordion is in a loading
                state.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  labelClassName
                </Text>
                : Additional CSS classes to apply custom styles to the label.
              </Text>
            </li>
          </ul>
        </Flex>

        {/* Examples */}
        <Flex direction="col" className="mt-5 w-full">
          <Title level={5}>Examples</Title>
          <Flex direction="col" className="gap-4 w-full">
            {/* Accordion  */}
            <ExpandableCard
              title=""
              description="Accordion "
              codeViewerProps={{
                code: AccordionExample1,
              }}
            >
              <Accordion
                items={[
                  {
                    label: "Djuno Design 1",
                    panel: "Panel content for Djuno Design 1",
                  },
                  {
                    label: "Djuno Design 2",
                    panel: "Panel content for Djuno Design 2",
                  },
                  {
                    label: "Djuno Design 3",
                    panel: "Panel content for Djuno Design 3",
                  },
                ]}
              />
            </ExpandableCard>
          </Flex>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5}>API</Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* items */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">items</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Array of accordion items, each containing a label and
                        optional panel content.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "IAccordion.ts#L37"}
                        size="xs"
                        code
                      >
                        {`Array<AccordionItem>`}
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">[]</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* classNames */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">classNames</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        CSS class names to apply to the container.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* panelClassNames */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">panelClassNames</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        CSS class names to apply to the panel.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* labelClassName */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">labelClassName</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Additional CSS classes to apply custom styles to the
                        label.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* loading */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">loading</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Indicates whether the accordion is in a loading state.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* loadingSetting */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">loadingSetting</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Configuration for the loading indicator, such as type or
                        position, when `loading` is `true`..
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Loading.ts#L26"}
                        size="xs"
                        code
                      >
                        LoadingProps
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* uiType */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">uiType</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Type of UI for the accordion.</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "IAccordion.ts#L38"}
                        size="xs"
                        code
                      >
                        AccordinType
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const AccordionExample1 = `<Accordion
  items={[
    {
      label: "Djuno Design 1",
      panel: "Panel content for Djuno Design 1",
    },
    {
      label: "Djuno Design 2",
      panel: "Panel content for Djuno Design 2",
    },
    {
      label: "Djuno Design 3",
      panel: "Panel content for Djuno Design 3",
    },
  ]}
/>`;

export default AccordionPage;
